import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Overlay from "../../../Overlay";
import Button from "../../../common/Button";
import { PAGE_STATE } from "../../../../constants";
import useUsers from "../../../../hooks/useUsers";
import { format } from "date-fns";

import { ReactComponent as UserSvg } from "../../../../assets/icons/user.svg";
import ComboBox from "../../../common/ComboBox";
import { Form, Formik } from "formik";
import TextBox from "../../../common/TextBox";
import FileUploader from "../../../common/FileUploader";
import { toast } from "react-toastify";
import ChangePassword from "./ChangePassword";

const assetsUri = import.meta.env.ASSETS_URI;

const View = ({ editing: _editing, onClose, onSuccess }) => {
  const [editing, setEditing] = useState(_editing);
  const [avatar, setAvatar] = useState(undefined);
  const [newAvatarFile, setNewAvatarFile] = useState(undefined);
  const [pageState, setPageState] = useState(
    editing ? PAGE_STATE.LOADING : PAGE_STATE.IDLE
  );
  const [savingState, setSavingState] = useState(PAGE_STATE.IDLE);
  const [initialValues, setInitialValues] = useState({
    no: "",
    firstname: "",
    lastname: "",
    email: "",
    joined: "",
    active: "true",
    password: "",
    rePassword: "",
  });

  const { queryStaffs, getStaff, patchStaff, createStaff, uploadStaffAvatar } =
    useUsers();

  useEffect(() => {
    if (!editing) return;
    fetchData();
    return () => {};
  }, []);

  const fetchData = async () => {
    const { avatar, firstname, lastname, createdOn, email, active } =
      await getStaff(editing);

    const _joined = createdOn ? format(new Date(createdOn), "dd/MM/yyyy") : "-";

    setInitialValues({
      no: 1,
      firstname,
      lastname,
      email: email || "",
      joined: _joined,
      active: active ? "true" : "false",
    });

    setAvatar(`${assetsUri}/${avatar}`);
    setPageState(PAGE_STATE.IDLE);
  };

  // * Handle avatar upload
  const handleFileChange = (files) => {
    if (files.length !== 1) return;

    setAvatar(URL.createObjectURL(files[0]));
    setNewAvatarFile(files[0]);
  };

  const handleValidate = ({
    firstname,
    lastname,
    email,
    password,
    confirmPassword,
  }) => {
    let errors = {};

    if (!firstname) errors.firstname = "Required";
    if (!lastname) errors.lastname = "Required";
    if (!email) errors.email = "Required";

    // * Validate password if creating
    if (!editing) {
      if (!password) errors.password = "Required";
      if (!confirmPassword) errors.confirmPassword = "Required";

      if (password !== confirmPassword) errors.confirmPassword = "Not matching";
    }

    return errors;
  };

  const handleSubmit = async (
    { firstname, lastname, email, active, password },
    options
  ) => {
    setSavingState(PAGE_STATE.LOADING);

    // * Edit staff
    if (editing) {
      try {
        await patchStaff(editing, {
          firstname,
          lastname,
          email,
          active: active === "true",
        });

        if (newAvatarFile) {
          await uploadStaffAvatar(editing, newAvatarFile);
        }

        setSavingState(PAGE_STATE.IDLE);
        onSuccess();
      } catch (error) {
        console.error(error.message);
        setSavingState(PAGE_STATE.ERROR);
      }
    }

    // * Create new staff
    if (!editing) {
      if (!avatar) {
        toast.warn("Please select a profile picture");
        return;
      }

      // * Check if user already exists
      const { data: existingStaffs } = await queryStaffs(
        { username: email, deleted: false },
        1,
        1
      );

      if (existingStaffs && existingStaffs.length > 0) {
        toast.error("Username already exists!");
        setSavingState(PAGE_STATE.IDLE);
        return;
      }

      try {
        const staff = await createStaff({
          firstname,
          lastname,
          email,
          username: email,
          password,
          active: active === "true",
          roles: "64c4d21c304cff63484c499e",
          deleted: true,
        });

        if (newAvatarFile) {
          await uploadStaffAvatar(staff._id, newAvatarFile);
          await patchStaff(staff._id, { deleted: false });
        }

        setSavingState(PAGE_STATE.IDLE);
        onSuccess();
      } catch (error) {
        console.error(error.message);
        setSavingState(PAGE_STATE.ERROR);
      }
    }
  };

  if (pageState === PAGE_STATE.LOADING) {
    return <Overlay />;
  }

  return (
    <Overlay
      style={{
        justifyContent: "flex-start",
      }}
      onClose={onClose}
      content={
        <div className="admin-staffs-view">
          <Formik
            initialValues={initialValues}
            validate={handleValidate}
            onSubmit={handleSubmit}
          >
            {({ isValid }) => {
              return (
                <Form autoComplete="off">
                  <h2 className="admin-staffs-view__header">
                    Staff Information
                  </h2>
                  <div className="admin-staffs-view-info">
                    <div className="admin-staffs-view-info-avatar">
                      {!avatar && (
                        <UserSvg className="admin-staffs-view-info-avatar__empty-state" />
                      )}
                      {avatar && (
                        <img
                          className="admin-staffs-view-info-avatar__image"
                          src={avatar}
                          alt="avatar"
                        />
                      )}
                    </div>
                    <FileUploader
                      id="avatar"
                      label={"Upload picture"}
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                    <TextBox label="Firstname" name="firstname" />
                    <TextBox label="Lastname" name="lastname" />
                    <TextBox
                      label="Username"
                      name="email"
                      autoComplete="new-email"
                    />
                    {!editing && (
                      <>
                        <TextBox
                          label="Password"
                          name="password"
                          type="password"
                          autoComplete="new-password"
                        />
                        <TextBox
                          label="Confirm Password"
                          name="confirmPassword"
                          type="password"
                          autoComplete="new-password"
                        />
                      </>
                    )}
                    <ComboBox
                      label="Status"
                      name="active"
                      options={[
                        { value: "true", name: "Active" },
                        { value: "false", name: "Inactive" },
                      ]}
                    />
                    {editing && (
                      <div className="admin-staffs-view-info-row">
                        <span className="admin-staffs-view-info-row__title">
                          Created on
                        </span>
                        <span className="admin-staffs-view-info-row__value">
                          {initialValues.joined}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="admin-staffs-view-footer">
                    {savingState === PAGE_STATE.LOADING && <>Saving...</>}
                    {savingState === PAGE_STATE.ERROR && <>Error saving</>}
                    {savingState === PAGE_STATE.IDLE && (
                      <Button
                        label={"Save"}
                        type="submit"
                        disabled={!isValid || !avatar}
                      />
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
          <ChangePassword staffId={editing} />
        </div>
      }
    />
  );
};

View.propTypes = {
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  editing: PropTypes.string,
};

View.defaultProps = {
  onClose: () => {},
  onSuccess: () => {},
  editing: undefined,
};

export default View;
